/**
 * Centre Health → Centre Ops → Overview: one centre's preschool work at a
 * glance (the "Alfred Adoption" sheet, design A1 — 26 Oct 2026). Section
 * cards of tiles, grouped as Shruti listed them: a ring is a share of what
 * was expected (done of due), a
 * square is a plain count. Every number comes from the same report endpoint
 * as the other tabs (oh_operations/centre_ops_report), one segment per call,
 * so a tile draws as soon as its segment arrives — `updates` is the slow one.
 * Clicking a tile opens the list behind its number (centre_ops_report/detail,
 * view only); a tile with nothing to list is not a button.
 */
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  fetchCentreOpsDetail,
  fetchCentreOpsRecord,
  fetchCentreOpsReport
} from 'api'
import DayCalendar, {
  DayCalendarLegend
} from 'app/components/generic/DayCalendar'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import ChecklistHistory from 'app/pages/Operations/Checklists/ChecklistHistory'
import { color } from 'app/theme/tokens'

// The ⓘ beside "Preschool Ops" (page band and the Preschool Quality & Ops
// card): what the tiles and the headline mean, in plain words (Satya, 28 Oct).
export const OPS_INFO = [
  'Each ring = work done ÷ work due at this centre for the dates picked, e.g. 18 of 20 lesson plans = 90%.',
  'A square = a plain count; nothing is due, so it has no %.',
  'Procurement & Expenses show the month’s spend against budget and its status; they are not scored.',
  'Green 85% and above · orange 60–84% · red below 60%.',
  'Lesson Planning, Weekly Observations and PTM Reports open a row per teacher; a teacher opens the Alfred page itself. Daily Updates opens the Daily Updates audit, By teacher.'
]

// Lesson Planning, Weekly Observations and PTM Reports: a row per teacher,
// and a teacher opens the Alfred page the work is done on, for this centre
// (Satya, 29 Oct: no second layer of our own, go to the real page).
const rupees = (n) =>
  n === null || n === undefined
    ? '–'
    : `₹${Math.round(n).toLocaleString('en-IN')}`
const mondayIso = (isoDay) => {
  const d = new Date(`${isoDay}T00:00:00`)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return iso(d)
}
const DAILY_INFO = [
  'Every child present in a preschool class is due their day’s updates: morning snack, lunch, care routine, learning moments, and evening snack for extended day.',
  'Each due update counts for every teacher of the child’s class, whoever posted it.',
  [
    'Due: updates due, up to yesterday',
    'Sent: posted, on time or late',
    'of which late: posted after the deadline',
    'Not sent: never posted'
  ],
  'Click a teacher to open the Daily Updates audit, By teacher.'
]
const TEACHER_WISE = {
  weekly_obs: {
    info: [
      'One weekly submission per child per week, counted for the teacher it is filed under.',
      'Full weeks only, up to last week: the week now running is not due yet.',
      [
        'Due: submissions for those weeks',
        'Sent: submitted',
        'Not sent: started but not submitted'
      ],
      'Click a teacher to open Weekly Submissions for her class.'
    ],
    done: (r) => r.status === 'Submitted',
    cols: ['Due', 'Sent', 'Not sent'],
    page: (centre, to, level) =>
      `/programs/preschool/weekly-submissions?centre=${centre}&date=${to}${
        level ? `&level=${level}` : ''
      }`
  },
  ptm: {
    info: [
      'One term report per child, made in Sep, Jan and April, counted for the teacher named on it.',
      [
        'Reports: term reports for the term',
        'Published: shared with the parent',
        'Not yet: not published'
      ],
      'Click a teacher to open Term Reports for her class.'
    ],
    done: (r) => r.status !== 'Not published',
    cols: ['Reports', 'Published', 'Not yet'],
    page: (centre, to, level) =>
      `/programs/preschool/term-report?centre=${centre}${
        level ? `&level=${level}` : ''
      }`
  }
}
const CENTRED = (cols) => cols.map((c) => ({ ...c, align: 'center' }))

// Lesson planning is counted in sessions per class (Satya, 29 Oct): the
// rows come per class, adding up to the tile; a row opens the planner.
const LESSON_INFO = [
  'Counted in sessions: each class owes its weekly sessions (e.g. Activity 1–4 × Mon–Fri) for every week that touches the period, if it has children enrolled.',
  'A plan belongs to the class, so its teachers are named together.',
  [
    'Sessions due: the weekly sessions × weeks',
    'Planned: sessions filled in the plan, draft or submitted',
    'Not planned: left empty, or no plan made'
  ],
  'Dots: green = all planned and submitted, amber = partly planned or still a draft, red = nothing planned.',
  'Click a class to open the Lesson Planner.'
]

export const teacherWise = (item, data, centre, to, centreName) => {
  if (item === 'lesson_plans') {
    const url = `/teacher/lesson-planner?centre=${centre}&week=${mondayIso(to)}`
    return {
      ...data,
      open_url: null,
      info: LESSON_INFO,
      columns: CENTRED(data.columns),
      rows: data.rows.map((r) => ({ ...r, _open: url }))
    }
  }
  // Daily Updates come counted per teacher; each row opens the audit page
  // By teacher (Satya, 29 Oct: like Lesson Planning, then the real page).
  if (item === 'daily_updates_teachers') {
    const url = dailyUpdatesUrl(centreName, to)
    return {
      ...data,
      info: DAILY_INFO,
      columns: CENTRED(data.columns),
      rows: data.rows.map((r) => ({ ...r, _open: url }))
    }
  }
  const how = TEACHER_WISE[item]
  if (!how) return data
  const by = {}
  data.rows.forEach((r) => {
    // A lesson plan is the class's: each of its teachers is credited.
    const names = r.teachers && r.teachers.length ? r.teachers : [r.teacher]
    names.forEach((n) => {
      const name = n || 'No teacher set'
      const t = (by[name] = by[name] || { name, due: 0, done: 0, levels: {} })
      t.due += 1
      if (how.done(r)) t.done += 1
      if (r.level) t.levels[r.level] = (t.levels[r.level] || 0) + 1
    })
  })
  const [dueCol, doneCol, notCol] = how.cols
  const rows = Object.values(by)
    .map((t) => {
      const level = Object.keys(t.levels).sort(
        (a, b) => t.levels[b] - t.levels[a]
      )[0]
      return {
        teacher: t.name,
        due: t.due,
        done: t.done,
        not: t.due - t.done,
        _open: how.page(centre, to, level)
      }
    })
    .sort((a, b) => b.not - a.not || a.teacher.localeCompare(b.teacher))
  return {
    ...data,
    // No "Open … page" link on top: each teacher's row opens the page
    // (Satya, 29 Oct).
    open_url: null,
    info: how.info,
    // The item's calendar stays on top, the teacher list under it (Satya,
    // 29 Oct); a day tapped lists its dots under the calendar.
    // Each column's heading and values centred (Satya, 29 Oct).
    columns: [
      { key: 'teacher', label: 'Teacher', align: 'center' },
      { key: 'due', label: dueCol, align: 'center' },
      { key: 'done', label: doneCol, align: 'center' },
      { key: 'not', label: notCol, align: 'center' }
    ],
    rows
  }
}

// The Daily Updates audit's By teacher tab, this centre, on the last school
// day up to `to` (yesterday at the latest: today is still being sent).
// "5th Oct 26" (Satya, 29 Oct: easier to read than 2026-10-05).
const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ')
export const niceDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  const th =
    d % 10 === 1 && d !== 11
      ? 'st'
      : d % 10 === 2 && d !== 12
      ? 'nd'
      : d % 10 === 3 && d !== 13
      ? 'rd'
      : 'th'
  return `${d}${th} ${MONTHS[m - 1]} ${String(y).slice(2)}`
}

export const dailyUpdatesUrl = (centreName, to) => {
  const d = new Date(`${to}T00:00:00`)
  const yesterday = new Date()
  yesterday.setHours(0, 0, 0, 0)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d > yesterday) d.setTime(yesterday.getTime())
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1)
  return `/audits/preschool-daily-updates?${new URLSearchParams({
    date: iso(d),
    centre: centreName,
    tab: 'teacher'
  })}`
}

export const InfoLines = ({ lines }) => (
  <div style={{ fontSize: 13, lineHeight: 1.45, maxWidth: 360 }}>
    {lines.map((line) =>
      Array.isArray(line) ? (
        <ul key={line.join()} style={{ margin: '2px 0 6px', paddingLeft: 18 }}>
          {line.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p key={line} style={{ margin: '4px 0' }}>
          {line}
        </p>
      )
    )}
  </div>
)

const SEGMENTS = ['checklist', 'interactions', 'parent', 'updates', 'activity']
// The API refuses longer periods (centre_ops_report.MAX_DAYS).
const MAX_DAYS = 400
const GOOD_FROM = 85
const WARN_FROM = 60
const RING = 2 * Math.PI * 26

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 864e5)

export const levelColor = (pct) => {
  if (pct === null || pct === undefined) return color.textFaint
  if (pct >= GOOD_FROM) return color.positive
  return pct >= WARN_FROM ? color.warning : color.negative
}

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', gap: 16 },
  band: {
    background: color.ink,
    color: '#fff',
    borderRadius: 16,
    padding: '20px 20px 56px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-end',
    justifyContent: 'space-between'
  },
  bandTitle: { fontSize: 22, fontWeight: 700 },
  bandSub: { fontSize: 13, color: '#D5D8E0' },
  bandHead: { display: 'flex', alignItems: 'center', gap: 8 },
  bandInfo: { fontSize: 20, color: '#D5D8E0', cursor: 'help' },
  bandScore: {
    fontSize: 14,
    fontWeight: 800,
    color: '#fff',
    borderRadius: 999,
    padding: '3px 12px'
  },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  field: {
    background: '#fff',
    borderRadius: 10,
    minWidth: 150,
    '& .MuiOutlinedInput-notchedOutline': { border: 0 },
    '& .MuiInputBase-input': { fontWeight: 600, color: color.ink }
  },
  label: { fontSize: 12, color: '#D5D8E0', marginBottom: 4 },
  // Inside Quality and Ops: no dark band, just ⓘ + Score on one slim line
  // (Satya, 28 Oct: the band left a big gap above the cards).
  slimHead: { display: 'flex', alignItems: 'center', gap: 8 },
  slimInfo: { fontSize: 20, color: color.textMuted, cursor: 'help' },
  cardsFlat: { display: 'flex', flexDirection: 'column', gap: 16 },
  cards: {
    marginTop: -48,
    padding: '0 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  card: {
    background: color.surface,
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 1px 2px rgba(40,46,60,.06), 0 4px 16px rgba(40,46,60,.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10 },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: color.brand
  },
  cardTitle: { fontSize: 16, fontWeight: 700, color: color.ink },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12
  },
  tile: {
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    background: color.cream,
    border: `1px solid ${color.border}`,
    borderRadius: 12,
    padding: 14,
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    minHeight: 96
  },
  // Parent Onboarding: one tile, two rings on one centre (Satya, 28 Oct);
  // each line opens its own list.
  pairLine: {
    font: 'inherit',
    fontSize: 13,
    color: '#3A4150',
    textAlign: 'left',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    '&:hover': { color: color.brand, textDecoration: 'underline' },
    '&:disabled': { cursor: 'default', textDecoration: 'none' }
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': { borderColor: color.brand }
  },
  square: {
    width: 64,
    height: 64,
    flex: 'none',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    fontWeight: 800,
    color: '#fff',
    background: color.ink
  },
  text: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  tileLabel: { fontSize: 14, fontWeight: 700, color: color.ink },
  tileXy: { fontSize: 13, fontWeight: 600, color: '#3A4150' },
  tileSub: { fontSize: 12, color: '#5F6470', lineHeight: 1.4 },
  status: {
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: 700,
    color: color.ink,
    background: color.border,
    borderRadius: 999,
    padding: '2px 8px'
  },
  stepRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 220px) 1fr 70px',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    padding: '4px 0'
  },
  barTrack: { height: 12, borderRadius: 6, background: color.border },
  barFill: { height: 12, borderRadius: 6 },
  ringBox: {
    width: 64,
    height: 64,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  muted: { fontSize: 12, color: color.textMuted },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  titleRight: { display: 'flex', alignItems: 'center', gap: 24 },
  center: { display: 'flex', justifyContent: 'center', padding: 24 },
  // The sticky heading stops at the scroll area's padding edge, so any top
  // padding leaves a strip where rows show through above it.
  dialogBody: { paddingTop: 0 },
  detailBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    marginBottom: 12
  },
  // Summary figures as small cards: number on top, label under it.
  stat: {
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'center',
    gap: 2,
    minWidth: 96,
    padding: '10px 16px',
    border: `1px solid ${color.border}`,
    borderRadius: 12,
    background: color.surface,
    fontSize: 13,
    '& strong': { fontSize: 22, lineHeight: 1.1 }
  },
  dayChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    margin: '4px 0 8px'
  },
  linkButton: {
    font: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    color: color.brand,
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer'
  },
  rowLink: { cursor: 'pointer' },
  dayBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    height: 180,
    borderBottom: `1px solid ${color.border}`,
    marginBottom: 4
  },
  dayBar: {
    flex: '1 1 0',
    minWidth: 6,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    '&:hover > span': { opacity: 0.8 }
  },
  dayNums: { display: 'flex', gap: 4, fontSize: 10, color: color.textMuted },
  typeRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 170px) 1fr 90px',
    gap: 12,
    alignItems: 'center',
    fontSize: 13,
    padding: '5px 0'
  },
  typeTrack: {
    display: 'flex',
    height: 14,
    borderRadius: 7,
    overflow: 'hidden',
    background: color.border
  },
  dayHead: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '4px 0 12px'
  },
  slack: { marginLeft: 6, verticalAlign: -3, color: color.brand },
  recGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 180px) 1fr',
    gap: '6px 16px',
    fontSize: 13,
    marginBottom: 16
  },
  recSection: { fontWeight: 700, fontSize: 14, margin: '12px 0 6px' },
  recText: { fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 10 },
  // No overflow box: the heading row sticks only when nothing between it and
  // the pop-up's scroll area clips it, so long cells wrap instead.
  // The theme's tables don't wrap, are 56px tall and have a 16px margin,
  // which pushed columns past the pop-up's right edge — undo all three here.
  table: {
    margin: 0,
    width: '100%',
    '& .MuiTableCell-root': {
      whiteSpace: 'normal',
      height: 'auto',
      padding: '6px 10px',
      overflowWrap: 'anywhere'
    },
    '& td': { fontSize: 13, verticalAlign: 'top' }
  }
}))

// Two rings on one centre: outer = all children, inner = new joiners.
const DoubleRing = ({ outer, inner }) => {
  const arc = (pct, r) => {
    const len = 2 * Math.PI * r
    const shown = pct === null || pct === undefined ? 0 : Math.min(pct, 100)
    return (
      <>
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={color.border}
          strokeWidth="6"
        />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={levelColor(pct)}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${((len * shown) / 100).toFixed(1)} ${len.toFixed(
            1
          )}`}
          transform="rotate(-90 32 32)"
        />
      </>
    )
  }
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      role="img"
      aria-label={`All children ${
        outer === null || outer === undefined ? 'none due' : `${outer} percent`
      }, new joiners ${
        inner === null || inner === undefined ? 'none due' : `${inner} percent`
      }`}
    >
      {arc(outer, 28)}
      {arc(inner, 18)}
    </svg>
  )
}

const Ring = ({ pct, neutral, stroke }) => {
  const shown = pct === null || pct === undefined ? 0 : Math.min(pct, 100)
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      role="img"
      aria-label={pct === null ? 'nothing due' : `${pct} percent`}
    >
      <circle
        cx="32"
        cy="32"
        r="26"
        fill="none"
        stroke={color.border}
        strokeWidth="7"
      />
      <circle
        cx="32"
        cy="32"
        r="26"
        fill="none"
        stroke={stroke || (neutral ? color.ink : levelColor(pct))}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${((RING * shown) / 100).toFixed(1)} ${RING.toFixed(
          1
        )}`}
        transform="rotate(-90 32 32)"
      />
      <text
        x="32"
        y="37"
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill={color.ink}
      >
        {pct === null || pct === undefined ? '–' : `${pct}%`}
      </text>
    </svg>
  )
}

// Parent Onboarding as bars: how many children are done at each step.
export const StepBars = ({ steps, classes }) => (
  <div style={{ marginBottom: 16 }}>
    {steps.map((st) => {
      const pct = st.of ? Math.round((st.done * 100) / st.of) : 0
      return (
        <div key={st.label} className={classes.stepRow}>
          <span>{st.label}</span>
          <span className={classes.barTrack}>
            <span
              className={classes.barFill}
              style={{
                display: 'block',
                width: `${pct}%`,
                background: levelColor(pct)
              }}
            />
          </span>
          <strong>
            {st.done} / {st.of}
          </strong>
        </div>
      )
    })}
  </div>
)

const LEGEND = [
  ['good', 'Done / on track'],
  ['warn', 'Partly'],
  ['bad', 'Missed / incident'],
  ['info', 'Planned / due']
]

// One tile's pop-up: optional summary figures, bars (Parent Onboarding) and
// Mon–Sun day calendars (the Teacher Scorecard look), then the rows under one heading row whose cells are the search
// boxes (column name in grey inside), stuck to the top of the pop-up as it
// scrolls — so no separate dropdown (Satya, 26 Oct). View only.
// A calendar day picked filters the rows to it; a row with a record opens it
// in a second window, view only; `_link` (an issue's Slack thread) is an arrow
// beside the first column (Satya, 27 Oct). `_open` (a newsletter) opens that
// Alfred page in a new tab, from the row or its arrow.
const dayLabel = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })

// Option A (Satya, 27 Oct): a bar per school day, the late part in amber;
// a bar opens that day (option B).
export const DayBars = ({ bars, classes, onDay }) => {
  const legendDot = (bg) => (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: 2,
        background: bg,
        marginRight: 6
      }}
    />
  )
  return (
    <div style={{ marginBottom: 16 }}>
      <div className={classes.dayBars}>
        {bars.map((b) => (
          <button
            type="button"
            key={b.date}
            className={classes.dayBar}
            title={`${dayLabel(b.date)} · ${b.sent} of ${b.due} sent (${
              b.pct
            }%), ${b.late} late`}
            aria-label={`Open ${dayLabel(b.date)}`}
            onClick={() => onDay(b.date)}
          >
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: `${b.due ? (b.sent * 100) / b.due : 0}%`,
                borderRadius: '3px 3px 0 0',
                overflow: 'hidden'
              }}
            >
              <span
                style={{
                  background: color.warning,
                  flex: `0 0 ${b.sent ? (b.late * 100) / b.sent : 0}%`
                }}
              />
              <span style={{ background: color.positive, flex: '1 1 auto' }} />
            </span>
          </button>
        ))}
      </div>
      <div className={classes.dayNums}>
        {bars.map((b) => (
          <span key={b.date} style={{ flex: '1 1 0', textAlign: 'center' }}>
            {Number(b.date.slice(8))}
          </span>
        ))}
      </div>
      <div className={classes.dayChip} style={{ color: color.textMuted }}>
        <span>{legendDot(color.positive)}On time</span>
        <span>{legendDot(color.warning)}Late</span>
        <span>Empty part = not sent · tap a day to open it</span>
      </div>
    </div>
  )
}

// Option B for one day: per update type, children on time / late / not sent.
export const TypeBars = ({ types, classes }) => (
  <div style={{ marginBottom: 16 }}>
    {types.map((t) => {
      const w = (n) => `${t.due ? (n * 100) / t.due : 0}%`
      return (
        <div key={t.key} className={classes.typeRow}>
          <strong>{t.label}</strong>
          <span className={classes.typeTrack}>
            <span style={{ width: w(t.on_time), background: color.positive }} />
            <span style={{ width: w(t.late), background: color.warning }} />
            <span style={{ width: w(t.missed), background: color.negative }} />
          </span>
          <span>
            {t.on_time + t.late} of {t.due}
          </span>
        </div>
      )
    })}
  </div>
)

const TICKS = new Set(['✓', '–', '-', '', null, undefined])

export const DetailList = ({
  data,
  from,
  to,
  classes,
  onOpen,
  onDay,
  legendSlot
}) => {
  const [day, setDay] = useState(null)
  const calendars =
    data.calendars ||
    (data.calendar && data.calendar.length
      ? [{ title: '', marks: data.calendar }]
      : [])
  const searchOf = useMemo(
    () => Object.fromEntries(data.columns.map((c) => [c.key, (r) => r[c.key]])),
    [data.columns]
  )
  const dayRows = useMemo(
    () => (day ? data.rows.filter((r) => r._iso === day) : data.rows),
    [data.rows, day]
  )
  const { rows, box, searching } = useColumnSearch(dayRows, searchOf)
  // A teacher searched in the list: the calendar keeps only her dots
  // (Satya, 29 Oct). Marks say whose they are in `teachers`.
  const shownCalendars = useMemo(() => {
    if (!searching) return calendars
    const names = new Set(rows.map((r) => r.teacher).filter(Boolean))
    return calendars.map((c) => ({
      ...c,
      marks: c.marks.filter(
        (m) => !m.teachers || m.teachers.some((t) => names.has(t))
      )
    }))
  }, [calendars, rows, searching])
  // ✓ / – columns get a dropdown (no tick on the keyboard). Yes / No
  // columns keep the search box: type yes or no (Satya, 28 Oct).
  const tickOptions = useMemo(() => {
    const out = {}
    data.columns.forEach((c) => {
      const vals = data.rows.map((r) => r[c.key])
      if (!vals.length) return
      if (vals.some((v) => v) && vals.every((v) => TICKS.has(v)))
        out[c.key] = [
          ['✓', '✓ Done'],
          ['–', '– Not done']
        ]
    })
    return out
  }, [data.columns, data.rows])
  const datedRows = data.rows.some((r) => r._iso)
  return (
    <>
      {!!(data.summary || []).length && (
        <div className={classes.detailBar}>
          {data.summary.map((s) => (
            <span key={s.label} className={classes.stat}>
              <span className={classes.muted}>{s.label}</span>
              <strong>{s.value}</strong>
            </span>
          ))}
        </div>
      )}
      {data.steps && data.steps.length > 0 && (
        <StepBars steps={data.steps} classes={classes} />
      )}
      {data.bars && data.bars.length > 0 && onDay && (
        <DayBars bars={data.bars} classes={classes} onDay={onDay} />
      )}
      {data.types && (
        <>
          <div className={classes.dayHead}>
            <span className={classes.muted}>
              Children who got each update, on time / late / not sent
            </span>
            {data.open_url && (
              <a
                href={data.open_url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.linkButton}
              >
                Open full details{' '}
                <OpenInNewIcon style={{ fontSize: 14, verticalAlign: -2 }} />
              </a>
            )}
          </div>
          <TypeBars types={data.types} classes={classes} />
        </>
      )}
      {calendars.length > 0 && (
        <>
          {/* The dot legend sits on the right of the pop-up's title row
              when there is one (Satya, 29 Oct: not on a line of its own). */}
          {(() => {
            const legend = (
              <DayCalendarLegend
                marks={shownCalendars.flatMap((c) => c.marks)}
                legend={LEGEND}
              />
            )
            return legendSlot ? createPortal(legend, legendSlot) : legend
          })()}
          {shownCalendars.map((c) => (
            <DayCalendar
              key={c.title || 'calendar'}
              title={c.title}
              marks={c.marks}
              shown={c.shown}
              from={from}
              to={to}
              onPick={datedRows ? setDay : undefined}
            />
          ))}
        </>
      )}
      {day && (
        <div className={classes.dayChip}>
          <strong>Showing {dayLabel(day)}</strong>
          <button
            type="button"
            className={classes.linkButton}
            onClick={() => setDay(null)}
          >
            Show all days
          </button>
        </div>
      )}
      {!data.columns.length ? (
        calendars.length ? null : (
          <Typography className={classes.muted}>
            Nothing in this period.
          </Typography>
        )
      ) : data.rows.length ? (
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              {/* Sl. no. first in every list (Satya, 29 Oct). */}
              <SearchHeadCell label="Sl" align="center" style={{ width: 48 }} />
              {data.columns.map((c) => (
                <SearchHeadCell
                  key={c.key}
                  label={c.label}
                  search={box(c.key)}
                  options={tickOptions[c.key]}
                  align={c.align}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              // A Daily Updates day row opens that day, like its bar.
              const dayRow = !!(data.bars && onDay && r._iso)
              return (
                <TableRow
                  key={i}
                  hover={!!(r._rec || r._open || dayRow)}
                  className={
                    r._rec || r._open || dayRow ? classes.rowLink : undefined
                  }
                  onClick={
                    r._open
                      ? () => window.open(r._open, '_blank', 'noopener')
                      : dayRow
                      ? () => onDay(r._iso)
                      : r._rec && onOpen
                      ? () => onOpen(r._rec)
                      : undefined
                  }
                >
                  <TableCell align="center">{i + 1}</TableCell>
                  {data.columns.map((c, ci) => (
                    <TableCell key={c.key} align={c.align}>
                      {r[c.key]}
                      {ci === 0 && r._link && (
                        <a
                          href={r._link}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open in Slack"
                          title="Open in Slack"
                          className={classes.slack}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon style={{ fontSize: 16 }} />
                        </a>
                      )}
                      {ci === 0 && r._open && (
                        <a
                          href={r._open}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open in a new tab"
                          title="Open in a new tab"
                          className={classes.slack}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon style={{ fontSize: 16 }} />
                        </a>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={data.columns.length + 1}>
                  <span className={classes.muted}>No rows match.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : (
        <Typography className={classes.muted}>
          Nothing in this period.
        </Typography>
      )}
    </>
  )
}

// One record in full (an incident, an observation), view only.
export const RecordView = ({ rec, classes }) => (
  <>
    <div className={classes.recGrid}>
      {rec.fields
        .filter((f) => f.value)
        .map((f) => (
          <React.Fragment key={f.label}>
            <span className={classes.muted}>{f.label}</span>
            <span>{f.value}</span>
          </React.Fragment>
        ))}
    </div>
    {(rec.sections || []).map((sec, si) => (
      <div key={`${sec.section}-${si}`}>
        {sec.section && (
          <Typography className={classes.recSection}>{sec.section}</Typography>
        )}
        <div className={classes.recGrid}>
          {sec.items.map((it, ii) => (
            <React.Fragment key={ii}>
              <span>{it.text}</span>
              <strong>{it.answer || '–'}</strong>
            </React.Fragment>
          ))}
        </div>
      </div>
    ))}
    {(rec.texts || [])
      .filter((t) => t.value)
      .map((t) => (
        <div key={t.label}>
          <Typography className={classes.recSection}>{t.label}</Typography>
          <Typography className={classes.recText}>{t.value}</Typography>
        </div>
      ))}
  </>
)

// Inside Preschool Quality & Ops the centre is shared with the Teacher
// Scorecard, so the wrapper may own it (centre + onCentre, its picker sits
// above both views) and reads the centre list and headline back.
const PreschoolOpsOverview = ({
  centre: sharedCentre,
  onCentre,
  onCentres,
  onSummary,
  hideCentre = false,
  headSlot,
  from: sharedFrom,
  to: sharedTo
} = {}) => {
  const classes = useStyles()
  const today = useMemo(() => new Date(), [])
  const [ownFrom, setFrom] = useState(
    iso(new Date(today.getFullYear(), today.getMonth(), 1))
  )
  const [ownTo, setTo] = useState(iso(today))
  const from = sharedFrom !== undefined ? sharedFrom : ownFrom
  const to = sharedTo !== undefined ? sharedTo : ownTo
  const [ownCentre, setOwnCentre] = useState('')
  const centre = sharedCentre !== undefined ? sharedCentre : ownCentre
  const setCentre = onCentre || setOwnCentre
  const [data, setData] = useState({})
  // {tile, state: loading | ok | error, data} — the open pop-up
  const [shown, setShown] = useState(null)
  const [legendSlot, setLegendSlot] = useState(null)
  // {state: loading | ok | error, data} — a row opened from the pop-up
  const [record, setRecord] = useState(null)
  const requestId = useRef(0)

  const rangeError =
    from > to
      ? 'From must be before To.'
      : daysBetween(from, to) >= MAX_DAYS
      ? `Pick at most ${MAX_DAYS} days.`
      : ''

  useEffect(() => {
    if (rangeError) return
    requestId.current += 1
    const mine = requestId.current
    setData(
      SEGMENTS.reduce((acc, s) => ({ ...acc, [s]: { state: 'loading' } }), {})
    )
    SEGMENTS.forEach((segment) => {
      fetchCentreOpsReport({ segment, from, to })
        .then((res) => {
          if (requestId.current !== mine) return
          const byUuid = {}
          ;(res.centres || []).forEach((row) => {
            byUuid[row.uuid] = row
          })
          setData((prev) => ({
            ...prev,
            [segment]: { state: 'ok', rows: res.centres || [], byUuid }
          }))
        })
        .catch(() => {
          if (requestId.current !== mine) return
          setData((prev) => ({
            ...prev,
            [segment]: { state: 'error', byUuid: {} }
          }))
        })
    })
  }, [from, to, rangeError])

  const centres = useMemo(() => {
    const first = SEGMENTS.map((s) => data[s] && data[s].rows).find(
      (rows) => rows && rows.length
    )
    return (first || [])
      .map((r) => ({ uuid: r.uuid, name: r.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  useEffect(() => {
    if (!centres.length) return
    if (!centres.find((c) => c.uuid === centre)) setCentre(centres[0].uuid)
  }, [centres])

  useEffect(() => {
    if (onCentres) onCentres(centres)
  }, [centres])

  const rowOf = (segment) =>
    data[segment] && data[segment].byUuid ? data[segment].byUuid[centre] : null
  const stateOf = (segment) => (data[segment] ? data[segment].state : 'loading')
  const act = (key) => {
    const row = rowOf('activity')
    return row && row.items ? row.items[key] : null
  }

  const checklist = rowOf('checklist')
  const parent = rowOf('parent')
  const updates = rowOf('updates')
  const interactions = rowOf('interactions')
  const onb = parent && parent.items ? parent.items.onboarding : null
  const daily = updates && updates.items ? updates.items.updates : null
  const newsletter = updates && updates.items ? updates.items.newsletter : null

  // `open` = what a click shows: {item} = the list from the detail endpoint,
  // {local} = what this page already holds (the checklist calendar), plus
  // optional {steps} drawn as bars above the list (Parent Onboarding).
  // No explanatory sub-lines on tiles (Satya, 26 Oct): the label says it.
  const ring = (label, segment, item, open, xy, status, scored = true) => ({
    kind: 'ring',
    label,
    segment,
    pct: item ? item.pct : null,
    xy: xy !== undefined ? xy : item ? `${item.done} of ${item.due}` : '',
    status,
    open,
    scored
  })
  // Out of the Ops score (Satya, 28 Oct): what the Teacher Scorecard already
  // scores per teacher (lesson plans, weekly submissions, daily updates).
  const unscored = (t) => ({
    ...t,
    scored: false,
    // Keeps its colour; no "Counted in Teacher Scorecard" line on the tile
    // (Satya, 29 Oct).
    teacherScored: true
  })
  const count = (label, segment, value, open) => ({
    kind: 'count',
    label,
    segment,
    value,
    open
  })
  const centreName = (centres.find((c) => c.uuid === centre) || {}).name || ''
  const trips = act('field_trips')
  const obs = act('observations')
  const money = act('money') || {}
  // Procurement & Expenses: the month's spend of budget and its status,
  // never scored (Satya, 29 Oct: "show value").
  const moneyTile = (key, label) => {
    const m = money[key]
    // Spend of budget as a ring: green within budget, red once over
    // (Satya, 29 Oct). Shown, never scored.
    const hasBudget =
      m && m.budget !== null && m.budget !== undefined && m.budget > 0
    const spentPct = hasBudget
      ? Math.round((100 * (m.spent || 0)) / m.budget)
      : null
    return {
      kind: 'money',
      pct: spentPct,
      over: hasBudget && (m.spent || 0) > m.budget,
      label,
      segment: 'activity',
      value: m
        ? `${rupees(m.spent)}${
            m.budget !== null && m.budget !== undefined
              ? ` of ${rupees(m.budget)}`
              : ''
          }`
        : '',
      status: m ? m.status || '' : '',
      open: { item: key }
    }
  }

  // Grouping = Shruti's list (26 Oct, 6:13 pm); Procurement & Expenses has a
  // tile per budget (Satya, 26 Oct).
  const sections = [
    {
      name: 'Academics & Learning',
      tiles: [
        unscored(
          ring('Lesson Planning', 'activity', act('lesson_plans'), {
            item: 'lesson_plans'
          })
        ),
        count('Field Trips', 'activity', trips ? trips.done : null, {
          item: 'field_trips'
        })
      ]
    },
    {
      name: 'Parent Experience',
      tiles: [
        // One tile, two rings (Satya, 28 Oct): all children, and the
        // children who joined in the period. Each ring opens its own list.
        {
          kind: 'pair',
          label: 'Parent Onboarding',
          segment: 'parent',
          parts: [
            {
              ...ring('Parent Onboarding', 'parent', parent, {
                item: 'onboarding',
                steps:
                  onb && onb.steps
                    ? onb.steps.map((st) => ({
                        label: st.label,
                        done: st.done,
                        of: onb.children
                      }))
                    : null
              }),
              short: 'All children'
            },
            {
              ...ring('New Joiners Onboarded', 'parent', onb ? onb.new : null, {
                item: 'onboarding_new'
              }),
              short: 'New joiners'
            }
          ]
        },
        unscored(
          ring(
            'Daily Updates (Moments)',
            'updates',
            daily && daily.tracked ? daily : null,
            { item: 'daily_updates_teachers' },
            daily && !daily.tracked ? 'not tracked here' : undefined
          )
        ),
        unscored(
          ring('Weekly Observations', 'activity', act('weekly_obs'), {
            item: 'weekly_obs'
          })
        ),
        count(
          'Monthly Newsletter',
          'updates',
          newsletter ? (newsletter.done ? 'Yes' : 'No') : null,
          { item: 'newsletter' }
        ),
        // Term reports are due in Sep, Jan and Apr only (Satya, 28 Oct): in
        // any other month the tile names the next month and is not scored.
        act('ptm') && act('ptm').next
          ? ring(
              'PTM Reports',
              'activity',
              null,
              { item: 'ptm' },
              `Next due: ${act('ptm').next}`,
              undefined,
              false
            )
          : ring('PTM Reports', 'activity', act('ptm'), { item: 'ptm' })
      ]
    },
    {
      name: 'Admissions',
      tiles: [
        ring('Interaction Audits', 'interactions', interactions, {
          item: 'interactions'
        })
      ]
    },
    {
      name: 'Team Management',
      tiles: [
        ring('Life Catch-ups', 'activity', act('catch_ups'), {
          item: 'catch_ups'
        })
      ]
    },
    {
      name: 'Centre Quality & Operations',
      tiles: [
        count(
          'Incidents / Accidents',
          'activity',
          act('incidents') ? act('incidents').n : null,
          { item: 'incidents' }
        ),
        ring('Maintenance Checklist', 'checklist', checklist, {
          // The Checklists › History calendar itself (Satya, 26 Oct)
          local: { title: 'Maintenance Checklist', checklistHistory: true }
        }),
        // Twice a month per teacher (Satya, 28 Oct): the ring = teachers
        // observed twice; both numbers under it. Scored per teacher in the
        // Teacher Scorecard, so not in the Ops score.
        unscored(
          ring(
            'Classroom Observations',
            'activity',
            obs && obs.teachers
              ? {
                  pct: Math.round((100 * obs.twice) / obs.teachers),
                  done: obs.twice,
                  due: obs.teachers
                }
              : null,
            { item: 'classroom_obs' },
            obs && obs.teachers
              ? `Once: ${obs.once} of ${obs.teachers} · Twice: ${obs.twice} of ${obs.teachers}`
              : obs
              ? `${obs.n} observations`
              : ''
          )
        )
      ]
    },
    {
      name: 'HQ Support',
      tiles: [
        count(
          'Open Issues',
          'activity',
          act('issues') ? act('issues').n : null,
          { item: 'issues' }
        )
      ]
    },
    {
      name: 'Procurement & Expenses',
      tiles: [
        moneyTile('program_material', 'Program Material'),
        moneyTile('consumables', 'Consumables'),
        moneyTile('petty_cash', 'Petty Cash'),
        moneyTile('repair_maintenance', 'Repair & Maintenance'),
        moneyTile('capex', 'Monthly Capex')
      ]
    }
  ]

  const openTile = (t) => {
    if (t.open.url) {
      window.open(t.open.url, '_blank', 'noopener')
      return
    }
    if (t.open.local) {
      setShown({ tile: t, state: 'ok', data: t.open.local })
      return
    }
    setShown({ tile: t, state: 'loading', data: null })
    fetchCentreOpsDetail({ item: t.open.item, centre, from, to })
      .then((data) =>
        setShown((prev) =>
          prev && prev.tile.label === t.label
            ? {
                ...prev,
                state: 'ok',
                data: {
                  ...teacherWise(t.open.item, data, centre, to, centreName),
                  steps: t.open.steps
                }
              }
            : prev
        )
      )
      .catch(() =>
        setShown((prev) =>
          prev && prev.tile.label === t.label
            ? { ...prev, state: 'error' }
            : prev
        )
      )
  }

  // A Daily Updates bar opens that day in the same window; Back returns.
  const openDay = (date) => {
    const back = shown
    const tile = { ...shown.tile, label: `${shown.tile.label} ${date}` }
    setShown({ tile, back, state: 'loading', data: null })
    fetchCentreOpsDetail({
      item: 'daily_updates_day',
      centre,
      from: date,
      to: date
    })
      .then((data) =>
        setShown((prev) =>
          prev && prev.tile.label === tile.label
            ? { ...prev, state: 'ok', data }
            : prev
        )
      )
      .catch(() =>
        setShown((prev) =>
          prev && prev.tile.label === tile.label
            ? { ...prev, state: 'error' }
            : prev
        )
      )
  }

  const openRecord = (rec) => {
    setRecord({ state: 'loading', data: null })
    fetchCentreOpsRecord({ ...rec, centre })
      .then((data) => setRecord({ state: 'ok', data }))
      .catch(() => setRecord({ state: 'error', data: null }))
  }

  const tileBody = (t) => {
    const state = t.segment ? stateOf(t.segment) : 'ok'
    let visual
    if (state === 'loading') {
      visual = (
        <span className={classes.ringBox}>
          <CircularProgress size={28} />
        </span>
      )
    } else if (t.kind === 'status') {
      visual = null
    } else if (t.kind === 'money') {
      visual = (
        <span className={classes.ringBox}>
          <Ring
            pct={state === 'error' ? null : t.pct}
            stroke={
              t.pct === null
                ? color.textFaint
                : t.over
                ? color.negative
                : color.positive
            }
          />
        </span>
      )
    } else if (t.kind === 'ring') {
      visual = (
        <span className={classes.ringBox}>
          <Ring
            pct={state === 'error' ? null : t.pct}
            neutral={t.kind === 'ring' && !t.scored && !t.teacherScored}
          />
        </span>
      )
    } else {
      visual = (
        <span className={classes.square}>
          {state === 'error' || t.value === null ? '–' : t.value}
        </span>
      )
    }
    return (
      <>
        {visual}
        <span className={classes.text}>
          <span className={classes.tileLabel}>{t.label}</span>
          {t.kind === 'ring' && t.xy && (
            <span className={classes.tileXy}>{t.xy}</span>
          )}
          {(t.kind === 'status' || t.kind === 'money') &&
            t.value &&
            state === 'ok' && <span className={classes.tileXy}>{t.value}</span>}
          {t.status && state === 'ok' && (
            <span className={classes.status}>{t.status}</span>
          )}
          {t.note && state === 'ok' && (
            <span className={classes.tileSub}>{t.note}</span>
          )}
          {state === 'error' && (
            <span className={classes.tileSub}>could not load</span>
          )}
        </span>
      </>
    )
  }

  // Headline = the plain average of the scored ring tiles that have
  // something due (Satya, 28 Oct: every ring counts the same). Left out:
  // count tiles, expense rings, and what the Teacher Scorecard scores.
  const rings = sections
    .flatMap((s) => s.tiles)
    .flatMap((t) => t.parts || [t])
    .filter(
      (t) =>
        t.kind === 'ring' &&
        t.scored &&
        stateOf(t.segment) === 'ok' &&
        t.pct !== null &&
        t.pct !== undefined
    )
  const ringAverage = rings.length
    ? Math.round(rings.reduce((sum, t) => sum + t.pct, 0) / rings.length)
    : null
  const pending = SEGMENTS.some((s) => stateOf(s) === 'loading')
  useEffect(() => {
    if (onSummary)
      onSummary({ pct: ringAverage, rings: rings.length, pending, rangeError })
  }, [ringAverage, rings.length, pending, rangeError])

  // Inside Preschool Quality & Ops the page's one Period picker sets the
  // dates (sharedFrom / sharedTo), so these fields are left out there.
  const dates = sharedFrom === undefined && (
    <>
      <div>
        <Typography className={classes.label}>From</Typography>
        <TextField
          type="date"
          size="small"
          variant="outlined"
          className={classes.field}
          value={from}
          onChange={(e) => e.target.value && setFrom(e.target.value)}
          inputProps={{ 'aria-label': 'From', max: to }}
        />
      </div>
      <div>
        <Typography className={classes.label}>To</Typography>
        <TextField
          type="date"
          size="small"
          variant="outlined"
          className={classes.field}
          value={to}
          onChange={(e) => e.target.value && setTo(e.target.value)}
          inputProps={{ 'aria-label': 'To', min: from, max: iso(today) }}
        />
      </div>
    </>
  )

  const wrapSlot = (node) => (headSlot ? createPortal(node, headSlot) : node)

  return (
    <div className={classes.root}>
      {hideCentre ? (
        // In the header row beside Period when the page gives a slot
        // (Satya, 28 Oct: not a row of its own under the tabs).
        wrapSlot(
          <div className={classes.slimHead}>
            <Tooltip
              placement="bottom-start"
              title={<InfoLines lines={OPS_INFO} />}
            >
              <InfoOutlinedIcon
                className={classes.slimInfo}
                aria-label="How Quality and Ops is scored"
              />
            </Tooltip>
            {/* No Score % here (Satya, 29 Oct). */}
          </div>
        )
      ) : (
        <div className={classes.band}>
          <div>
            <Typography className={classes.bandSub}>Quality and Ops</Typography>
            <Typography className={classes.bandTitle}>
              {centreName || 'Loading…'}
            </Typography>
          </div>
          <div className={classes.filters}>
            <div>
              <Typography className={classes.label}>Centre</Typography>
              <TextField
                select
                size="small"
                variant="outlined"
                className={classes.field}
                value={centre}
                onChange={(e) => setCentre(e.target.value)}
                inputProps={{ 'aria-label': 'Centre' }}
              >
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            {dates}
          </div>
        </div>
      )}

      <div className={hideCentre ? classes.cardsFlat : classes.cards}>
        {rangeError && (
          <div className={classes.card}>
            <Typography color="error">{rangeError}</Typography>
          </div>
        )}
        {!rangeError &&
          sections.map((s) => (
            <section key={s.name} className={classes.card}>
              <div className={classes.cardHead}>
                <span className={classes.dot} />
                <Typography component="h2" className={classes.cardTitle}>
                  {s.name}
                </Typography>
              </div>
              <div className={classes.grid}>
                {s.tiles.map((t) =>
                  t.kind === 'pair' ? (
                    <div key={t.label} className={classes.tile}>
                      <span className={classes.ringBox}>
                        {stateOf(t.segment) === 'loading' ? (
                          <CircularProgress size={28} />
                        ) : (
                          <DoubleRing
                            outer={t.parts[0].pct}
                            inner={t.parts[1].pct}
                          />
                        )}
                      </span>
                      <span className={classes.text}>
                        <span className={classes.tileLabel}>{t.label}</span>
                        {t.parts.map((part, i) => (
                          <button
                            type="button"
                            key={part.label}
                            className={classes.pairLine}
                            disabled={stateOf(part.segment) !== 'ok'}
                            onClick={() => openTile(part)}
                          >
                            {i === 0 ? 'Outer' : 'Inner'} · {part.short}:{' '}
                            <b>
                              {part.pct === null || part.pct === undefined
                                ? '–'
                                : `${part.pct}%`}
                            </b>{' '}
                            ({part.xy})
                          </button>
                        ))}
                      </span>
                    </div>
                  ) : t.open && stateOf(t.segment) === 'ok' ? (
                    <button
                      type="button"
                      key={t.label}
                      className={`${classes.tile} ${classes.clickable}`}
                      onClick={() => openTile(t)}
                    >
                      {tileBody(t)}
                    </button>
                  ) : (
                    <div key={t.label} className={classes.tile}>
                      {tileBody(t)}
                    </div>
                  )
                )}
              </div>
            </section>
          ))}
      </div>

      <Dialog open={!!shown} onClose={() => setShown(null)} fullScreen>
        {shown && (
          <>
            <DialogTitle disableTypography className={classes.dialogTitle}>
              <span>
                {shown.back && (
                  <button
                    type="button"
                    className={classes.linkButton}
                    onClick={() => setShown(shown.back)}
                  >
                    ← All days
                  </button>
                )}
                <Typography className={classes.tileLabel}>
                  {(shown.data && shown.data.title) || shown.tile.label}
                  {/* How the numbers are counted (Satya, 29 Oct). */}
                  {shown.data && shown.data.info && (
                    <Tooltip
                      placement="bottom-start"
                      title={<InfoLines lines={shown.data.info} />}
                    >
                      <InfoOutlinedIcon
                        className={classes.slimInfo}
                        style={{ marginLeft: 8, verticalAlign: -3 }}
                        aria-label="How this is counted"
                      />
                    </Tooltip>
                  )}
                </Typography>
                <Typography className={classes.muted}>
                  {centreName} · {niceDate(from)} to {niceDate(to)}
                  {shown.data && shown.data.note ? ` · ${shown.data.note}` : ''}
                </Typography>
                {shown.data && shown.data.open_url && !shown.data.types && (
                  <a
                    href={shown.data.open_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.linkButton}
                  >
                    Open {shown.tile.label} page{' '}
                    <OpenInNewIcon
                      style={{ fontSize: 14, verticalAlign: -2 }}
                    />
                  </a>
                )}
              </span>
              <span className={classes.titleRight}>
                <span ref={setLegendSlot} />
                <IconButton
                  size="small"
                  onClick={() => setShown(null)}
                  aria-label="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </DialogTitle>
            <DialogContent className={classes.dialogBody}>
              {shown.state === 'loading' && (
                <div className={classes.center}>
                  <CircularProgress size={28} />
                </div>
              )}
              {shown.state === 'error' && (
                <Typography color="error">Could not load the list.</Typography>
              )}
              {shown.state === 'ok' && shown.data.checklistHistory && (
                <ChecklistHistory
                  initialCentre={centre}
                  initialMonth={to}
                  embedded
                />
              )}
              {shown.state === 'ok' && !shown.data.checklistHistory && (
                <DetailList
                  key={shown.tile.label}
                  data={shown.data}
                  from={from}
                  to={to}
                  classes={classes}
                  onOpen={openRecord}
                  onDay={openDay}
                  legendSlot={legendSlot}
                />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!record}
        onClose={() => setRecord(null)}
        fullWidth
        maxWidth="sm"
      >
        {record && (
          <>
            <DialogTitle disableTypography className={classes.dialogTitle}>
              <span>
                <Typography className={classes.tileLabel}>
                  {record.data ? record.data.title : 'Loading…'}
                </Typography>
                {record.data && record.data.subtitle && (
                  <Typography className={classes.muted}>
                    {centreName} · {record.data.subtitle}
                  </Typography>
                )}
              </span>
              <IconButton
                size="small"
                onClick={() => setRecord(null)}
                aria-label="Close record"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {record.state === 'loading' && (
                <div className={classes.center}>
                  <CircularProgress size={28} />
                </div>
              )}
              {record.state === 'error' && (
                <Typography color="error">
                  Could not open this record.
                </Typography>
              )}
              {record.state === 'ok' && (
                <RecordView rec={record.data} classes={classes} />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  )
}

export default PreschoolOpsOverview
